import React, { useState, useEffect, useContext } from "react"
import { Box, IconButton, Stack, useTheme } from "@mui/material"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded"
import ClearIcon from "@mui/icons-material/Clear"
import PageContainer from "src/components/page/PageContainer"
import { useKmProps } from "src/km/hooks/useKmProps"
import { UserContext } from "src/providers"
import { postTabletVisited } from "src/services/api/metrics"
import { useResponsive } from "src/utils/useResponsive"
import { TaxonomyPair, Params } from "../components/KmControls"
import { kmAsk, kmCreate } from "../km"
import { KmMessageList } from "../components/KmMessageList"
import KmControls from "../components/KmControls"
import KmSettingsModal from "../components/KmSettingsModal"
import { KmUserInput } from "../components/KmUserInput"
import { useChatState } from "../hooks/useChatState"
import { useModels } from "../hooks/useModels"
import { useTaxonomies } from "../hooks/useTaxonomies"
import { usePrompts } from "../hooks/usePrompts"
import CustomAutocomplete from "src/components/CustomAutocomplete"

export default function KmPage({
  generate,
  initialPlaceholder,
  placeholder,
  defaultMessage
}: {
  generate?: boolean
  initialPlaceholder: string
  placeholder: string
  defaultMessage: () => string
}) {
  const { isMobile } = useResponsive()
  const { palette } = useTheme()
  const { user, companyPortalProperties } = useContext(UserContext)
  const companyId = user?.company?.companyId
  const realTaxonomyKey = companyPortalProperties.taxonomy
  const genre = companyPortalProperties.genre

  const { query, setQuery, loading, messageState, handleSubmit, clear, noQuestionsYet } = useChatState({
    storageKey: `chat.history.${companyId}.${generate ? "gen" : "chat"}`,
    defaultMessage: defaultMessage
  })
  const { taxonomies, selectedTaxonomy, setSelectedTaxonomy } = useTaxonomies()
  const { models, model, setModel } = useModels({ endpoint: "/api/km/models" })
  const { languages, lang, setLang } = useKmProps()
  const { params, setParams } = usePrompts({ generate })
  const [temperature, setTemperature] = useState(3)
  const [settingsModalOpen, setSettingsModalOpen] = useState(false)
  const [isInputFocused, setIsInputFocused] = useState(false)

  useEffect(() => {
    postTabletVisited(generate ? "Exfluency CREATE" : "Exfluency ASK")
  }, [generate])

  // Wrapper that clears chat state when taxonomy changes
  const handleSetSelectedTaxonomy = (taxonomy: TaxonomyPair) => {
    if (taxonomy.code === selectedTaxonomy.code) {
      return
    }
    setSelectedTaxonomy(taxonomy)
    clear()
  }

  if (!model || !companyId) {
    return <></>
  }

  // Create a wrapper for handleSubmit that calls the hook's handleSubmit with postQuestion
  const handleSubmitWrapper = (e: any) => {
    handleSubmit(e, postQuestion)
  }

  const postQuestion = (question: string, history: string[]) => {
    const q = selectedTaxonomy.code === "general" ? `\\qa ${question}` : question
    const askRequest = {
      question: q,
      history: history,
      model: model.model,
      taxonomy: selectedTaxonomy.code,
      lang: lang.languageId,
      company_id: companyId,
      genre: genre,
      realTaxonomyKey: realTaxonomyKey
    }
    return (
      generate
        ? kmCreate({
            ...askRequest,
            prompt: params.selectedPrompt.prompt,
            temperature: (temperature - 1) / 4
          })
        : kmAsk(askRequest)
    ).then((response) => response.data)
  }

  const kmUserInput = (
    <KmUserInput
      minRows={isMobile ? 3 : 1}
      query={query}
      setQuery={setQuery}
      loading={loading}
      placeholder={placeholder}
      initialPlaceholder={initialPlaceholder}
      modelSelected={!!model}
      noQuestionsYet={noQuestionsYet}
      handleSubmit={handleSubmitWrapper}
      clear={clear}
      skipEndAdornment={isMobile}
      onFocusChange={setIsInputFocused}
    />
  )

  return (
    <PageContainer>
      <Box
        sx={{
          // height: { xs: "calc(100dvh - 80px)", md: "calc(100dvh - 96px)", lg: "calc(100dvh - 128px)" },
          overflow: "hidden",
          minHeight: 0,
          flexDirection: "column",
          display: "flex",
          flex: 1
        }}
      >
        <Box
          sx={{
            py: { xs: 1, md: 4 },
            px: 1,
            flexGrow: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "white"
          }}
        >
          <KmMessageList
            messages={messageState.messages}
            botIcon={"/km/exfluency-icon.svg"}
            showSources={true}
            loading={loading}
            companyId={companyId}
            selectedTaxonomyCode={selectedTaxonomy?.code}
            chatbot={false}
            query={query}
            isInputFocused={isInputFocused}
          />
        </Box>
        {!isMobile && (
          <Box
            sx={{
              p: { xs: 2, md: 4 },
              px: { xs: 1, md: 4 },
              boxShadow: "0px -5px 15px #0719391A",
              flexShrink: 0
            }}
          >
            <KmControls
              generate={generate}
              taxonomies={taxonomies}
              selectedTaxonomy={selectedTaxonomy}
              setSelectedTaxonomy={handleSetSelectedTaxonomy}
              lang={lang}
              setLang={setLang}
              languages={languages}
              model={model}
              setModel={setModel}
              models={models}
              params={params}
              temperature={temperature}
              setParams={setParams}
              setTemperature={setTemperature}
            />

            {kmUserInput}
          </Box>
        )}

        {isMobile && (
          <Box
            sx={{
              pt: { xs: 2, md: 4 },
              boxShadow: "0px -5px 15px #0719391A",
              flexShrink: 0
            }}
          >
            <Stack direction="column" spacing={2}>
              {kmUserInput}
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{
                  backgroundColor: "#F5F5F5",
                  px: { xs: 2, md: 4 },
                  py: { xs: 2 },
                  // pb: 4 to workaround problem with the keyboard in mobile
                  pb: { xs: 4, md: 4 }
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  {/* Template Selection for Generate mode */}
                  {generate && params && (
                    <Box sx={{ width: "180px" }}>
                      <CustomAutocomplete
                        data-testid="km-select-prompt-mobile"
                        size="small"
                        value={params.selectedPrompt.title}
                        options={params.prompts.map((prompt) => prompt.title)}
                        getOptionLabel={(option) => option}
                        onChange={(e, val) => {
                          if (val) {
                            const selectedPrompt = params.prompts.find((prompt) => prompt.title === val)
                            if (selectedPrompt) {
                              setParams((prevParams: Params) => ({
                                ...prevParams,
                                selectedPrompt: selectedPrompt
                              }))
                            }
                          }
                        }}
                        isOptionEqualToValue={(o1, o2) => o1 === o2}
                        disableClearable
                      />
                    </Box>
                  )}
                  {!generate && taxonomies && handleSetSelectedTaxonomy && (
                    <Box sx={{ width: "180px" }}>
                      <CustomAutocomplete
                        data-testid="km-select-taxonomy"
                        label="Select Knowledge Base"
                        size="extraSmall"
                        value={selectedTaxonomy}
                        options={taxonomies}
                        getOptionLabel={(option) => option.name}
                        onChange={(e, val) => val && handleSetSelectedTaxonomy(val as TaxonomyPair)}
                        isOptionEqualToValue={(o1, o2) => o1.code === o2.code}
                        groupBy={(option) => option.group}
                        disableClearable
                      />
                    </Box>
                  )}

                  <IconButton
                    onClick={() => setSettingsModalOpen(true)}
                    aria-label="open settings"
                    sx={{ width: "40px", height: "40px", p: 0 }}
                  >
                    <SettingsOutlinedIcon sx={{ fontSize: 35, stroke: "#F5F5F5", strokeWidth: 0.5 }} />
                  </IconButton>
                </Stack>

                <Stack direction="row" spacing={{ xs: 0.5, md: 1 }} alignItems="center">
                  {
                    <IconButton
                      data-testid="km-send-message-button"
                      disabled={loading || !query || !model}
                      onClick={handleSubmitWrapper}
                      title="Send message"
                      sx={{
                        width: "35px",
                        height: "35px",
                        color: palette.text.primary,
                        background: palette.primary.main,
                        "&.Mui-disabled": {
                          backgroundColor: "#E2E2E2",
                          color: "#BABABA"
                        }
                      }}
                    >
                      <ArrowForwardRoundedIcon />
                    </IconButton>
                  }
                  {clear && !noQuestionsYet && (
                    <IconButton
                      data-testid="km-clear-messages-button"
                      disabled={loading || noQuestionsYet}
                      onClick={clear}
                      title="Clear context"
                      sx={{
                        width: "35px",
                        height: "35px",
                        backgroundColor: "#E2E2E2",
                        "&.Mui-disabled": {
                          backgroundColor: "#E2E2E2",
                          color: "#BABABA"
                        }
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Box>
        )}
      </Box>

      {/* Settings Modal for Mobile */}
      <KmSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        generate={generate}
        taxonomies={taxonomies}
        selectedTaxonomy={selectedTaxonomy}
        setSelectedTaxonomy={handleSetSelectedTaxonomy}
        lang={lang}
        setLang={setLang}
        languages={languages}
        model={model}
        setModel={setModel}
        models={models}
        params={params}
        temperature={temperature}
        setParams={setParams}
        setTemperature={setTemperature}
      />
    </PageContainer>
  )
}
